import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { ShieldCheck, Eye, Lock, Search, BarChart2, BookOpen, Loader2 } from 'lucide-react';

// This component is for the "Quick Tips" section
const InfoCard = ({ icon, title, children }) => (
  <div className="glass-card p-6 rounded-xl h-full">
    <div className="flex items-center text-cyan-400 mb-3">
      {icon}
      <h3 className="ml-3 text-xl font-bold text-white">{title}</h3>
    </div>
    <p className="text-gray-400 leading-relaxed">{children}</p>
  </div>
);

// This component is for the "In-Depth Guides" links
const GuideLinkCard = ({ icon, title, description, to }) => (
  <Link to={to} className="block glass-card p-6 rounded-xl hover:bg-white/5 transition-all group">
    <div className="flex items-center text-purple-400 mb-3">
      {icon}
      <h3 className="ml-3 text-xl font-bold text-white group-hover:text-purple-300">{title}</h3>
    </div>
    <p className="text-gray-400 leading-relaxed">{description}</p>
  </Link>
);

export default function LearnMorePage() {
    const [dynamicArticles, setDynamicArticles] = useState([]);
    const [loading, setLoading] = useState(true);

    // This useEffect now correctly fetches the dynamic articles once
    useEffect(() => {
        const fetchArticles = async () => {
            const q = query(
                collection(db, 'learning_articles'),
                where('published', '==', true),
                orderBy('createdAt', 'desc')
            );
            const snapshot = await getDocs(q);
            setDynamicArticles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        };
        fetchArticles();
    }, []);

  return (
    <div className="space-y-12">
      <div className="text-center">
        <h1 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
          Learn & Stay Safe
        </h1>
        <p className="text-lg text-gray-300 max-w-3xl mx-auto">
          Knowledge is your best defense. Browse our guides to understand common scams and protect your digital assets.
        </p>
      </div>

      {/* --- RESTORED: Quick Tips Section --- */}
      <div>
        <h2 className="text-3xl font-bold text-white mb-6 text-center">Quick Tips</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <InfoCard icon={<ShieldCheck size={24} />} title="Do Your Own Research">
            Before investing, research the team, read the whitepaper, and check community reviews.
          </InfoCard>
          <InfoCard icon={<Eye size={24} />} title="Spot Phishing Attempts">
            Scammers create fake websites or emails to steal your info. Always double-check URLs.
          </InfoCard>
          <InfoCard icon={<Lock size={24} />} title="Protect Your Private Keys">
            Never share your private keys or seed phrase. Anyone who asks for them is a scammer.
          </InfoCard>
        </div>
      </div>

      {/* --- Core Guides (Static Links) --- */}
      <div>
        <h2 className="text-3xl font-bold text-white mb-6 text-center">Core Guides</h2>
        <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6">
          <GuideLinkCard to="/learn/researching-projects" icon={<Search size={24} />} title="Researching Projects" description="A step-by-step guide on how to properly investigate a cryptocurrency project."/>
          <GuideLinkCard to="/learn/spotting-scams" icon={<Eye size={24} />} title="Spotting Scam Tactics" description="Learn to identify common red flags and warning signs used by scammers." />
          <GuideLinkCard to="/learn/on-chain-analysis" icon={<BarChart2 size={24} />} title="Intro to On-Chain Data" description="A beginner's guide to using public blockchain data to verify information."/>
        </div>
      </div>

      {/* --- New & Updated Articles (Dynamic from Firestore) --- */}
      {(loading || dynamicArticles.length > 0) && (
        <div>
            <h2 className="text-3xl font-bold text-white mb-6 text-center pt-12">New & Updated Articles</h2>
            {loading ? (
                <div className="flex justify-center"><Loader2 className="animate-spin text-cyan-400" /></div>
            ) : (
                <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6">
                    {dynamicArticles.map(article => (
                        <GuideLinkCard
                            key={article.id}
                            to={`/learn/${article.slug}`}
                            icon={<BookOpen size={24} />}
                            title={article.title}
                            description="Click to read the full guide."
                        />
                    ))}
                </div>
            )}
        </div>
      )}
    </div>
  );
}